import React, {useState} from 'react';
import Translate from "../../components/Translate";
import {formatSecondsToHHMMSS} from "../../utils/General";
import PlatformIcon from "./PlatformIcon";
import Tabs from "../ui/Tabs";
import ClockArrowUpIcon from "../../assets/icons/ClockArrowUpIcon";
import SettingsIcon from "../../assets/icons/SettingsIcon";
import PersonIcon from "../../assets/icons/PersonIcon";
import ForumIcon from "../../assets/icons/ForumIcon";
import OperatorProfileDrawer from "./OperatorProfileDrawer";
import './ConversationDetails.css';
import {formatBackendDateTime} from '../../utils/DateUtils';

const style = {
    container: {
        backgroundColor: 'var(--ds-bg-primary)',
        fontSize: '14px',
        margin: '0 auto',
        maxWidth: '600px',
        width: '440px',
        maxHeight: '100%',
        minHeight: '480px',
        overflowY: 'auto',
        transition: 'all 0.3s ease',
        scrollbarWidth: 'thin',
    },
    section: {
        marginBottom: '25px',
        paddingBottom: '15px',
    },
    row: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '10px',
        alignItems: 'center',
        fontSize: '12px',
        padding: '6px 8px',
        borderRadius: '8px',
        transition: 'background-color 0.2s ease',
    },
    rowHover: {
        backgroundColor: 'rgba(0,0,0,0.02)',
    },
    label: {
        fontWeight: '600',
        color: 'var(--ds-text-primary)',
    },
    value: {
        textAlign: 'right',
        color: '#4a4a4a',
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        flex: 1,
        marginLeft: '15px',
        maxWidth: '50%',
    },
    operatorButton: {
        display: 'inline',
        padding: 0,
        border: 0,
        background: 'transparent',
        color: 'var(--ds-text-primary)',
        font: 'inherit',
        cursor: 'pointer',
    },
};

const ratingMap = {
    1: '⭐',
    2: '⭐⭐',
    3: '⭐⭐⭐',
    4: '⭐⭐⭐⭐',
    5: '⭐⭐⭐⭐⭐',
};

const InfoRow = ({label, value}) => {
    const [hovered, setHovered] = useState(false);

    return (
        <div
            style={{
                ...style.row,
                ...(hovered ? style.rowHover : {}),
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <span style={style.label}><Translate text={label}/>:</span>
            <span style={style.value}>
                {value}
            </span>
        </div>
    );
};

/*
 * @param initialTab which tab to open on. The panel is reached from more than
 *   one place now, and where it was opened from says what the reader came for:
 *   the info button means the conversation, the visitor's name means the visitor.
 */
const ConversationDetails = ({conversationDetails, initialTab = 'chat', parentCompanyId, canViewOperatorProfile = false}) => {

    const [activeTab, setActiveTab] = useState(initialTab);
    const [operatorProfile, setOperatorProfile] = useState(null);

    const {
        conversationId,
        status,
        operatorId,
        operatorFullName,
        chatWithOperatorRequestCount,
        customerName,
        visitorPlatformId,
        visitorEmail,
        visitorPhoneNumber,
        visitorContactPhoneNumber,
        visitorDevice,
        visitorBrowser,
        visitorOsVersion,
        visitorIp,
        firstResponseTime,
        conversationTime,
        avrgResponseTime,
        date,
        lastModifiedAt,
        ratingScore,
        categoryName,
        subject,
        description,
        parentSiteUrl,
        botName,
        lastChoseSkillName,
        messagesCount,
        requestToOperatorDate,
        operatorJoinedDate
    } = conversationDetails;

    const openOperatorProfile = () => {
        if (!canViewOperatorProfile || !operatorId || !parentCompanyId || operatorFullName === 'null null') return;
        const nameParts = (operatorFullName || '').trim().split(/\s+/).filter(Boolean);
        setOperatorProfile({
            id: operatorId,
            fullName: operatorFullName,
            firstName: nameParts[0],
            lastName: nameParts.slice(1).join(' '),
        });
    };

    return (
        <div style={style.container}>

            {/* the same control as every other tab group in the app - it was the
                one caller still asking for the old 'flat' look, and the rule
                under it went with that look */}
            <div style={{marginBottom: '24px'}}>
                <Tabs
                    variant='underline'
                    selectedTab={activeTab}
                    onTabChange={setActiveTab}
                    tabs={[
                        {value: 'chat', icon: <ForumIcon width={16} height={16}/>},
                        {value: 'user', icon: <PersonIcon width={16} height={16}/>},
                        {value: 'time', icon: <ClockArrowUpIcon width={16} height={16}/>},
                        {value: 'other', icon: <SettingsIcon width={16} height={16}/>},
                    ]}
                    style={{width: 'fit-content', margin: '0 auto'}}
                />
            </div>

            {activeTab === 'chat' && (
                <div style={style.section}>
                    <InfoRow label="id" value={conversationId}/>
                    <InfoRow label="bot" value={botName}/>
                    <InfoRow label="platform" value={<PlatformIcon conversation={conversationDetails} size={30}/>}/>
                    <InfoRow label="Status" value={<Translate text={status}/>}/>
                    <InfoRow
                        label="operator"
                        value={
                            canViewOperatorProfile && operatorFullName !== 'null null' && operatorId && parentCompanyId ? (
                                <button
                                    type="button"
                                    className="conversation-details__operator-button"
                                    style={style.operatorButton}
                                    onClick={openOperatorProfile}
                                >
                                    {operatorFullName}
                                </button>
                            ) : (
                                operatorFullName !== 'null null' ? operatorFullName : '-'
                            )
                        }
                    />
                    <InfoRow label="lastChoseSkill" value={lastChoseSkillName || 'N/A'}/>
                    <InfoRow label="messageCount" value={messagesCount}/>
                    <InfoRow label="parentSiteUrl" value={parentSiteUrl}/>
                </div>
            )}

            {activeTab === 'user' && (
                <div style={style.section}>
                    <InfoRow label="user" value={customerName}/>
                    <InfoRow label="id" value={visitorPlatformId}/>
                    <InfoRow label="email" value={visitorEmail || 'N/A'}/>
                    <InfoRow label="phoneNumber" value={visitorPhoneNumber || 'N/A'}/>
                    {visitorContactPhoneNumber && (
                        <InfoRow
                            label="contactPhoneNumber"
                            value={visitorContactPhoneNumber}
                        />
                    )}
                    <InfoRow label="ip" value={visitorIp || 'N/A'}/>
                    <InfoRow label="device" value={visitorDevice || 'N/A'}/>
                    <InfoRow label="browser" value={visitorBrowser || 'N/A'}/>
                    <InfoRow label="osVersion" value={visitorOsVersion || 'N/A'}/>
                    <InfoRow label="operatorRequestCount" value={chatWithOperatorRequestCount || 'N/A'}/>
                </div>
            )}

            {activeTab === 'time' && (
                <div style={style.section}>
                    <InfoRow label="firstResponseTime" value={formatSecondsToHHMMSS(firstResponseTime)}/>
                    <InfoRow label="conversationTime" value={formatSecondsToHHMMSS(conversationTime)}/>
                    <InfoRow label="avrgResponseTime" value={formatSecondsToHHMMSS(avrgResponseTime)}/>
                    <InfoRow label="createdAt" value={formatBackendDateTime(date)}/>
                    <InfoRow label="lastModifiedAt" value={formatBackendDateTime(lastModifiedAt)}/>
                    <InfoRow label="requestToOperatorDate"
                             value={formatBackendDateTime(requestToOperatorDate)}/>
                    <InfoRow label="operatorJoinedDate" value={formatBackendDateTime(operatorJoinedDate)}/>
                </div>
            )}

            {activeTab === 'other' && (
                <div>
                    <div style={style.section}>
                        <InfoRow
                            label="Rating"
                            value={
                                !ratingScore || ratingScore === 0 ? (
                                    'N/A'
                                ) : (
                                    <span>
                                        {ratingMap[ratingScore]}
                                    </span>
                                )
                            }
                        />
                        <InfoRow label="category" value={categoryName || 'N/A'}/>
                        <InfoRow label="subject" value={subject || 'N/A'}/>
                        <div style={{...style.row, alignItems: 'flex-start'}}>
                            <span style={style.label}><Translate text="description"/>:</span>
                            <span style={{...style.value, maxWidth: '60%'}}>{description || 'N/A'}</span>
                        </div>
                    </div>

                </div>
            )}

            <OperatorProfileDrawer
                open={canViewOperatorProfile && !!operatorProfile}
                onClose={() => setOperatorProfile(null)}
                operator={operatorProfile}
                parentCompanyId={parentCompanyId}
            />
        </div>
    );
};

export default ConversationDetails;



// WEBPACK FOOTER //
// ./src/components-new/general/ConversationDetails.jsx